import { listLastUserCompanies } from 'shared/api/services/LastUserCompanyService'
import { getDirectoryUser } from 'shared/api/services/UsersService'

export function normalizeId(value) {
  return String(value ?? '').trim()
}

export function getStoredUser() {
  try {
    const stored = localStorage.getItem('userDetails')
    const parsed = stored ? JSON.parse(stored) : null
    return parsed?.user ?? parsed ?? null
  } catch {
    return null
  }
}

export function getCurrentUserId() {
  const storedUser = getStoredUser()
  return storedUser?.id ?? storedUser?.user?.id ?? storedUser?.userId ?? ''
}

export function getStoredActiveBusinessId() {
  try {
    const stored = localStorage.getItem('activeEvent')
    const parsed = stored ? JSON.parse(stored) : null
    return String(parsed?.activeBusinessId || parsed?.businessId || '').trim()
  } catch {
    return ''
  }
}

export function extractList(payload) {
  if (!payload) return []
  if (Array.isArray(payload)) return payload
  const direct = payload?.data ?? payload?.items ?? payload?.result
  if (Array.isArray(direct)) return direct
  const nested = direct?.data ?? direct?.items ?? direct?.result
  if (Array.isArray(nested)) return nested
  return []
}

export function resolveCompanyLabel(item) {
  return String(item?.companyName || item?.name || item?.brandName || item?.businessName || '').trim()
}

export function resolveBusinessIdFromItem(item) {
  return normalizeId(item?.id || item?.businessId)
}

/*
 * Kullanicinin firmalari UC ayri kaynaktan gelebiliyor:
 *   1. `/businesses?mine=true` — sahip / olusturan / `business_user_links`
 *   2. `/users/directory/:id` -> `businesses`  — sahip olduklari
 *   3. `/users/directory/:id` -> `assignments` — `business_user_assignments`
 *
 * ⚠️ (3) `mine=true` tarafindan KAPSANMIYOR. Yalnizca atama ile bir firmaya
 * bagli kullanicilar (ki cogunluk boyle) tek basina `mine=true` ile BOS liste
 * aliyor, acilir kutu `disabled` kaliyordu.
 */
export function mergeCompaniesById(groups) {
  const byId = new Map()
  groups.forEach((item) => {
    const id = resolveBusinessIdFromItem(item)
    if (!id || byId.has(id)) return
    byId.set(id, item)
  })
  return Array.from(byId.values())
}

export function extractDirectoryCompanies(payload) {
  const directoryUser = payload?.data?.data ?? payload?.data ?? null
  const businesses = Array.isArray(directoryUser?.businesses) ? directoryUser.businesses : []
  const assignments = Array.isArray(directoryUser?.assignments) ? directoryUser.assignments : []
  return [...businesses, ...assignments]
}

/**
 * Kullanicinin yonetebildigi firmalari UC kaynagi birlestirerek doner.
 * Her iki kaynak da basarisiz olursa `failed: true` doner (cagiran hata
 * mesaji gostermek isteyebilir); tek kaynak basarisiz olursa sessizce
 * digeriyle devam edilir.
 */
export async function fetchOwnedCompanies(currentUserId) {
  const [ownedCompaniesRes, directoryUserRes] = await Promise.all([
    listLastUserCompanies({ mine: true }).catch(() => null),
    currentUserId ? getDirectoryUser(currentUserId, undefined).catch(() => null) : Promise.resolve(null),
  ])
  if (!ownedCompaniesRes && !directoryUserRes) {
    return { companies: [], failed: true }
  }
  const companies = mergeCompaniesById([
    ...extractList(ownedCompaniesRes?.data),
    ...extractDirectoryCompanies(directoryUserRes),
  ])
  return { companies, failed: false }
}

/**
 * Firma secimini gunceller: mevcut secim hala gecerliyse korunur; kullanici
 * elle bos secim yaptiysa (touched) otomatik atama yapilmaz; aksi halde
 * localStorage'daki son aktif firma, o da yoksa ilk sahip olunan firma
 * secilir.
 */
export function pickSelectedCompanyId({ ownedCompanies, currentSelectedId, touched }) {
  const ownedBusinessIds = ownedCompanies.map((item) => resolveBusinessIdFromItem(item)).filter(Boolean)
  const normalizedCurrent = normalizeId(currentSelectedId)
  if (normalizedCurrent && ownedBusinessIds.includes(normalizedCurrent)) return normalizedCurrent
  if (touched) return ''
  const storedBusinessId = getStoredActiveBusinessId()
  return [storedBusinessId, ...ownedBusinessIds].find((id) => id && ownedBusinessIds.includes(id)) || ''
}
